import { AttributesObject } from "@/types";
import { ShopAttributes } from "@prisma/client";
import { isValidPhoneNumber } from "libphonenumber-js";

export function getToday() {
    const today = new Date(
        new Date().toLocaleString("en-US", { timeZone: "Europe/Istanbul" }),
    );
    const startOfDay = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
    );
    const endOfDay = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + 1,
    );

    return { today, startOfDay, endOfDay };
}

export function safeUrl(url?: string | null) {
    if (!url) {
        return null;
    }
    const regex = new RegExp("^https?://");
    if (regex.test(url)) {
        return encodeURI(url);
    }
    if (url[0] != "/") {
        return "/" + encodeURI(url);
    }
    return encodeURI(url);
}

export function isEmail(value?: string) {
    if (!value) {
        return false;
    }
    const validRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    return value.match(validRegex);
}

export function decimalPlaces(num: number | null, dp: number) {
    if (num === null) return "-";
    return Math.round(num * Math.pow(10, dp)) / Math.pow(10, dp);
}

export function getFormattedImageUrl(url: string | null | undefined) {
    // Same normalization as safeUrl, but returns "" (not null) for empty input
    // since callers use it directly as an <img> src.
    return safeUrl(url) ?? "";
}

export function objectToSearchParams(params: {
    [key: string]: string | string[] | undefined;
}): URLSearchParams {
    const searchParams = new URLSearchParams();
    const keys = Object.keys(params).sort();
    for (const key of keys) {
        if (params.hasOwnProperty(key)) {
            const value = params[key];
            if (value !== undefined) {
                if (Array.isArray(value)) {
                    value.forEach((val) => searchParams.append(key, val));
                } else {
                    searchParams.set(key, value);
                }
            }
        }
    }
    return searchParams;
}

export function positiveIntSearchParam(
    value: string | string[] | undefined,
    fallback: number,
) {
    const parsed = Number.parseInt(
        Array.isArray(value) ? value[0] : (value ?? ""),
        10,
    );

    return parsed > 0 ? parsed : fallback;
}

export function secondsToHMS(
    totalSeconds: number | null | undefined,
    showHours: boolean | null | undefined = false,
) {
    if (typeof showHours !== "boolean") {
        showHours = false;
    }

    if (totalSeconds === 0) {
        if (showHours) {
            return "0:00:00";
        }
        return "00:00";
    }

    if (!totalSeconds) {
        if (showHours) {
            return "--:--:--";
        }
        return "--:--";
    }

    if (showHours) {
        const hours = Math.floor(totalSeconds / 3600);
        totalSeconds = totalSeconds % 3600;

        const minutes = Math.floor(totalSeconds / 60);
        totalSeconds = totalSeconds % 60;

        const seconds = Math.floor(totalSeconds);

        return (
            (hours < 10 ? "0" : "") +
            hours +
            ":" +
            (minutes < 10 ? "0" : "") +
            minutes +
            ":" +
            (seconds < 10 ? "0" : "") +
            seconds
        );
    }

    const minutes = Math.floor(totalSeconds / 60);
    totalSeconds = totalSeconds % 60;

    const seconds = Math.floor(totalSeconds);

    return (
        (minutes < 10 ? "0" : "") +
        minutes +
        ":" +
        (seconds < 10 ? "0" : "") +
        seconds
    );
}

export function getShopAttributesObject(
    shopAttributes: ShopAttributes[] | undefined,
) {
    const attributes: AttributesObject = {
        backgroundUrl: null,
        storeNotice: null,
        overridedTrainerId: null,
        overridedCampaignText: null,
    };

    shopAttributes?.map((attributeObj: ShopAttributes) => {
        const name = attributeObj.AttributeName as keyof AttributesObject;
        const value = attributeObj.AttributeValue;

        if (name) {
            attributes[name] = value as never;
        }
    });

    return attributes;
}

export function getFormattedViewCounts(views: number | null | undefined) {
    if (!views || views === null) {
        return "0";
    }
    if (views >= 1000000) {
        return (views / 1000000).toFixed(1) + "Mn";
    } else if (views >= 1000) {
        return (views / 1000).toFixed(1) + "B";
    } else {
        return views.toString();
    }
}

export const playerTextColors: Record<string, string> = {
    GM: "text-[#ba1717]",
    WGM: "text-[#ba1717]",
    IM: "text-[#3818d9]",
    WIM: "text-[#3818d9]",
    FM: "text-[#e9c200]",
    WFM: "text-[#e9c200]",
    CM: "text-[#cd6b2e]",
    WCM: "text-[#cd6b2e]",
};

export const playerBadgeColors: Record<string, string> = {
    GM: "bg-[#ba1717]",
    WGM: "bg-[#ba1717]",
    IM: "bg-[#3818d9]",
    WIM: "bg-[#3818d9]",
    FM: "bg-[#e9c200]",
    WFM: "bg-[#e9c200]",
    CM: "bg-[#cd6b2e]",
    WCM: "bg-[#cd6b2e]",
};

export const playerBorderColors: Record<string, string> = {
    GM: "border-[#ba1717]",
    WGM: "border-[#ba1717]",
    IM: "border-[#3818d9]",
    WIM: "border-[#3818d9]",
    FM: "border-[#e9c200]",
    WFM: "border-[#e9c200]",
    CM: "border-[#cd6b2e]",
    WCM: "border-[#cd6b2e]",
};

export function validatePhoneNumber(phoneNumber: string): boolean {
    return isValidPhoneNumber(phoneNumber);
}
